import type { DocumentIntelligence } from '@/shared/components/attachments/AttachmentRedactionEditor/documentIntelligence';
import type { Memo } from '@/types';

import type {
    TranscriptExecutive,
    TranscriptOntologyAnnotation,
    TranscriptPeriod,
    TranscriptQuoteSelection,
    TranscriptSection,
    TranscriptSourceEntity,
    TranscriptSourceIdentity,
} from './types';

const normalizeSpeakerName = (value: string) => value.replace(/\s+/g, ' ').trim().toLocaleLowerCase();

const escapePattern = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const TITLE_SIGNAL_PATTERN =
    /\b(?:analyst|chair|chief|co-?founder|director|executive|founder|general counsel|head|investor relations|officer|partner|president|senior vice president|svp|vice president|vp)\b/i;

const cleanTitleCandidate = (value: string) =>
    value
        .replace(/^[\s,;:–—-]+/, '')
        .replace(/[,;:]?\s+(?:and|with)\s*$/i, '')
        .replace(/[\s,;:–—-]+$/, '')
        .replace(/\s+/g, ' ')
        .trim();

const inferredTitleForSpeaker = (speaker: string, introduction: string, participantNames: string[]) => {
    const speakerExpression = new RegExp(escapePattern(speaker), 'i');

    for (const match of introduction.matchAll(new RegExp(speakerExpression.source, 'gi'))) {
        const afterName = introduction.slice((match.index ?? 0) + match[0].length);
        const delimiter = afterName.match(/^\s*(?:,|[-–—])\s*/);

        if (!delimiter) {
            continue;
        }

        const titleStart = delimiter[0].length;
        const afterDelimiter = afterName.slice(titleStart);
        const boundaryCandidates = [afterDelimiter.search(/[.;]\s|\n/)]
            .concat(
                participantNames
                    .filter((name) => normalizeSpeakerName(name) !== normalizeSpeakerName(speaker))
                    .map((name) =>
                        afterDelimiter.search(new RegExp(`(?:,?\\s+(?:and|with)\\s+)?${escapePattern(name)}`, 'i')),
                    ),
            )
            .filter((index) => index >= 0);
        const titleEnd = boundaryCandidates.length
            ? Math.min(...boundaryCandidates)
            : Math.min(afterDelimiter.length, 160);
        const candidate = cleanTitleCandidate(afterDelimiter.slice(0, titleEnd));
        const candidateWordCount = candidate.split(/\s+/).filter(Boolean).length;

        if (
            candidate.length >= 2 &&
            candidate.length <= 120 &&
            candidateWordCount <= 18 &&
            TITLE_SIGNAL_PATTERN.test(candidate) &&
            !/\b(?:announced|asked|joined|joining|said|says|speaking|thank|thanks|welcome|will|would)\b/i.test(
                candidate,
            )
        ) {
            return candidate;
        }
    }

    return undefined;
};

export const getTranscriptSpeakerTitles = (
    sections: TranscriptSection[],
    executives: TranscriptExecutive[],
): ReadonlyMap<string, string> => {
    const speakers = [...new Set(sections.map((section) => section.speaker.trim()).filter(Boolean))];
    const executiveByName = new Map(executives.map((executive) => [normalizeSpeakerName(executive.name), executive]));
    const titles = new Map<string, string>();

    speakers.forEach((speaker) => {
        const normalizedSpeaker = normalizeSpeakerName(speaker);
        const executive = [...executiveByName.entries()].find(
            ([normalizedName]) =>
                normalizedSpeaker.includes(normalizedName) || normalizedName.includes(normalizedSpeaker),
        )?.[1];

        if (executive?.title) {
            titles.set(normalizedSpeaker, executive.title);
        }
    });

    const introduction = sections
        .slice(0, 8)
        .map((section) => section.content)
        .join('\n')
        .slice(0, 20_000);
    const participantNames = [
        ...new Set([
            ...speakers.filter((speaker) => speaker.split(/\s+/).length >= 2),
            ...executives.map((executive) => executive.name),
        ]),
    ].sort((left, right) => right.length - left.length);

    speakers.forEach((speaker) => {
        const normalizedSpeaker = normalizeSpeakerName(speaker);

        if (titles.has(normalizedSpeaker) || speaker.split(/\s+/).length < 2) {
            return;
        }

        const inferredTitle = inferredTitleForSpeaker(speaker, introduction, participantNames);

        if (inferredTitle) {
            titles.set(normalizedSpeaker, inferredTitle);
        }
    });

    return titles;
};

export const getTranscriptSpeakerTitle = (titles: ReadonlyMap<string, string>, speaker: string) =>
    titles.get(normalizeSpeakerName(speaker));

const ontologyAnnotationsOverlap = (left: TranscriptOntologyAnnotation, right: TranscriptOntologyAnnotation) =>
    left.start < right.end && left.end > right.start;

export const buildTranscriptOntologyAnnotations = (
    sections: TranscriptSection[],
    intelligence: DocumentIntelligence,
): ReadonlyMap<string, readonly TranscriptOntologyAnnotation[]> => {
    const domainById = new Map(intelligence.domains.map((domain) => [domain.id, domain]));
    const annotations = new Map<string, TranscriptOntologyAnnotation[]>();
    const orderedSections = [...sections].sort((left, right) => left.start - right.start);
    const findSection = (start: number, end: number) => {
        let lowerBound = 0;
        let upperBound = orderedSections.length - 1;

        while (lowerBound <= upperBound) {
            const midpoint = Math.floor((lowerBound + upperBound) / 2);
            const section = orderedSections[midpoint];

            if (start < section.start) {
                upperBound = midpoint - 1;
            } else if (start >= section.end) {
                lowerBound = midpoint + 1;
            } else {
                return end <= section.end ? section : undefined;
            }
        }

        return undefined;
    };

    intelligence.concepts.forEach((concept) => {
        concept.references.forEach((reference) => {
            const section = findSection(reference.textStart, reference.textEnd);

            if (!section) {
                return;
            }

            annotations.set(section.id, [
                ...(annotations.get(section.id) ?? []),
                {
                    concept,
                    domain: concept.primaryDomainId ? domainById.get(concept.primaryDomainId) : undefined,
                    end: reference.textEnd - section.start,
                    reference,
                    start: reference.textStart - section.start,
                },
            ]);
        });
    });

    return new Map(
        [...annotations.entries()].map(([sectionId, sectionAnnotations]) => {
            const resolved: TranscriptOntologyAnnotation[] = [];

            sectionAnnotations
                .sort((left, right) => left.start - right.start || right.end - right.start - (left.end - left.start))
                .forEach((annotation) => {
                    if (!resolved.some((candidate) => ontologyAnnotationsOverlap(candidate, annotation))) {
                        resolved.push(annotation);
                    }
                });

            return [sectionId, resolved.sort((left, right) => left.start - right.start)];
        }),
    );
};

const recordValue = (value: unknown, keys: string[]) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return undefined;
    }

    const record = value as Record<string, unknown>;

    return keys.map((key) => record[key]).find((candidate) => candidate !== null && candidate !== undefined);
};

const numericValue = (value: unknown) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value !== 'string') {
        return undefined;
    }

    const parsed = Number(value.replace(/[^0-9]/g, ''));

    return Number.isFinite(parsed) ? parsed : undefined;
};

const stringValue = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim() : undefined);

export const normalizeFmpCollection = (payload: unknown): unknown[] => {
    if (Array.isArray(payload)) {
        return payload;
    }

    const nestedData = recordValue(payload, ['data']);

    if (Array.isArray(nestedData)) {
        return nestedData;
    }

    return payload === null || payload === undefined ? [] : [payload];
};

export const getTranscriptPeriod = (value: unknown): TranscriptPeriod | undefined => {
    const tuple = Array.isArray(value) ? value : undefined;
    const quarter = numericValue(tuple?.[0] ?? recordValue(value, ['quarter', 'fiscalQuarter', 'period']));
    const year = numericValue(tuple?.[1] ?? recordValue(value, ['year', 'fiscalYear', 'calendarYear']));

    if (!quarter || !year || quarter < 1 || quarter > 4) {
        return undefined;
    }

    return {
        date: stringValue(tuple?.[2] ?? recordValue(value, ['date', 'calendarDate', 'filingDate'])),
        quarter,
        raw: value,
        year,
    };
};

export const getTranscriptText = (value: unknown) =>
    stringValue(recordValue(value, ['content', 'transcript', 'text'])) ?? '';

export const getTranscriptExecutives = (payload: unknown): TranscriptExecutive[] =>
    normalizeFmpCollection(payload).flatMap((value) => {
        const name = stringValue(recordValue(value, ['name']));

        return name ? [{ name, title: stringValue(recordValue(value, ['title'])) }] : [];
    });

export const parseTranscriptSections = (text: string): TranscriptSection[] => {
    if (!text.trim()) {
        return [];
    }

    const speakerRegex = /(^|\n)([A-Z][A-Za-z.'-]*(?:\s+[A-Z][A-Za-z.'-]*)*|Operator):\s*/g;
    const matches = Array.from(text.matchAll(speakerRegex));

    if (!matches.length) {
        const leadingWhitespace = text.match(/^\s*/)?.[0].length ?? 0;
        const content = text.trim();

        return [
            {
                content,
                end: leadingWhitespace + content.length,
                id: 'section-0',
                speaker: 'Transcript',
                start: leadingWhitespace,
            },
        ];
    }

    return matches.flatMap((match, index) => {
        const matchStart = match.index ?? 0;
        const contentStart = matchStart + match[0].length;
        const nextMatchStart = matches[index + 1]?.index ?? text.length;
        const rawContent = text.slice(contentStart, nextMatchStart);
        const leadingWhitespace = rawContent.match(/^\s*/)?.[0].length ?? 0;
        const content = rawContent.trim();

        if (!content) {
            return [];
        }

        const start = contentStart + leadingWhitespace;

        return [
            {
                content,
                end: start + content.length,
                id: `section-${index}`,
                speaker: match[2]?.trim() || 'Speaker',
                start,
            },
        ];
    });
};

export const getSelectionOffsets = (container: HTMLElement, range: Range) => {
    if (!container.contains(range.startContainer) || !container.contains(range.endContainer)) {
        return undefined;
    }

    const selectedText = range.toString();
    const leadingWhitespace = selectedText.match(/^\s*/)?.[0].length ?? 0;
    const trailingWhitespace = selectedText.match(/\s*$/)?.[0].length ?? 0;
    const quote = selectedText.trim();

    if (!quote) {
        return undefined;
    }

    const leadingRange = range.cloneRange();
    leadingRange.selectNodeContents(container);
    leadingRange.setEnd(range.startContainer, range.startOffset);
    const start = leadingRange.toString().length + leadingWhitespace;

    return { end: start + selectedText.length - leadingWhitespace - trailingWhitespace, quote, start };
};

export const escapeHtml = (value: string) =>
    value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

const safeHttpUrl = (value?: string) => {
    if (!value) {
        return undefined;
    }

    try {
        const normalizedValue = /^[a-z][a-z\d+.-]*:\/\//i.test(value) ? value : `https://${value}`;
        const url = new URL(normalizedValue);

        return ['http:', 'https:'].includes(url.protocol) ? url.toString() : undefined;
    } catch {
        return undefined;
    }
};

const entityInitials = (value: string) =>
    value
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join('');

export const getTranscriptLabel = ({ quarter, year }: Pick<TranscriptSourceIdentity, 'quarter' | 'year'>) =>
    `Q${quarter} ${year} Earnings Call`;

export const buildTranscriptSourceUrl = ({
    identity,
    pathname,
    selection,
}: {
    identity: TranscriptSourceIdentity;
    pathname: string;
    selection: Pick<TranscriptQuoteSelection, 'end' | 'sectionId' | 'start'>;
}) => {
    const url = new URL(pathname, window.location.origin);
    url.searchParams.set('symbol', identity.symbol);
    url.searchParams.set('year', String(identity.year));
    url.searchParams.set('quarter', String(identity.quarter));
    url.searchParams.set('section', selection.sectionId);
    url.searchParams.set('start', String(selection.start));
    url.searchParams.set('end', String(selection.end));

    return url.toString();
};

export const getTranscriptQuoteHtml = ({
    identity,
    quote,
    sourceEntity,
    sourceUrl,
}: {
    identity: TranscriptSourceIdentity;
    quote: string;
    sourceEntity?: TranscriptSourceEntity;
    sourceUrl: string;
}) => {
    const companyName = sourceEntity?.name?.trim() || identity.symbol;
    const citationLabel = getTranscriptLabel(identity);
    const logoUrl = safeHttpUrl(sourceEntity?.logoUrl?.trim());
    const website = safeHttpUrl(sourceEntity?.website?.trim());
    const logoAttribute = logoUrl ? ` data-logo-url="${escapeHtml(logoUrl)}"` : '';
    const websiteAttribute = website ? ` data-website="${escapeHtml(website)}"` : '';

    return [
        '<blockquote class="blockquote-light filing-source-quote" contenteditable="false">',
        `<p class="filing-source-quote-text" contenteditable="false" data-filing-quote-text="true">${escapeHtml(quote.replace(/\s+/g, ' ').trim())}</p>`,
        '<p class="filing-source-citation">',
        `<a class="filing-source-button" data-filing-source="true" data-avatar="${escapeHtml(entityInitials(companyName) || identity.symbol[0] || 'T')}" data-company="${escapeHtml(companyName)}" data-filing="${escapeHtml(citationLabel)}" href="${escapeHtml(sourceUrl)}"${logoAttribute}${websiteAttribute}>`,
        `<span class="filing-source-company">${escapeHtml(companyName)}</span>`,
        '<span aria-hidden="true" class="filing-source-chevron"></span>',
        `<span class="filing-source-filing">${escapeHtml(citationLabel)}</span>`,
        '</a>',
        '</p>',
        '</blockquote>',
        '<p></p>',
    ].join('');
};

export const memoReferencesTranscript = (memo: Pick<Memo, 'body'>, identity: TranscriptSourceIdentity) => {
    if (!memo.body || typeof DOMParser === 'undefined') {
        return false;
    }

    const document = new DOMParser().parseFromString(memo.body, 'text/html');

    return Array.from(document.querySelectorAll<HTMLAnchorElement>('a[href]')).some((anchor) => {
        try {
            const url = new URL(anchor.href, window.location.origin);

            return (
                url.searchParams.get('symbol')?.toLocaleUpperCase() === identity.symbol.toLocaleUpperCase() &&
                url.searchParams.get('year') === String(identity.year) &&
                url.searchParams.get('quarter') === String(identity.quarter)
            );
        } catch {
            return false;
        }
    });
};
